import { c as _c } from "react/compiler-runtime";
import { DateRangePicker, getDate, setDateWithTimezone, Tooltip } from '@systemeio/ui';
import ContactPaymentsPaymentTooltip from 'modules/contacts/contact-page/components/contact-paymnet-tooltip';
import { useContactSubscriptions } from 'modules/contacts/contact-page/hooks/use-contact-subscriptions';
import { ContactPageFilterInterface } from 'modules/contacts/contact-page/types/contact-page-filter-interface';
import { ContactSubscriptionsInterface } from 'modules/contacts/contact-page/types/contact-subscriptions-interface';
import { getContactPaymentUrl } from 'modules/contacts/contact-page/utils/get-contact-payment-url';
import { ContactId, ContactInterface } from 'modules/contacts/types/contact-interface';
import UnsubscribeModal from 'modules/payment/subscriptions/components/unsubscribe-modal';
import { SubscriptionStatusEnum } from 'modules/payment/subscriptions/enums/subscription-status-enum';
import React, { useEffect } from 'react';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import ListingLayout from 'shared/components/listing-layout';
import { Table } from 'shared/components/table/table';
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { useFilterPagination } from 'shared/route-query/hooks/use-filter-pagination';
import { OrderEnum } from 'shared/types/pagination-interface';
import { dateFormat, dateTimeFormat } from 'shared/utils/date-time-format';
import { getDateRangeSinceDate, getToday } from 'shared/utils/get-dates';
import { BlockedIcon, CheckIcon, PercentIcon } from '@systemeio/ui/icons';
interface ContactSubscriptionsProps {
  contactId: ContactId;
  contact?: ContactInterface;
}
const defaultFilters: ContactPageFilterInterface = {
  date: [null, null]
};
const SubscriptionStatus = t0 => {
  const $ = _c(27);
  const {
    status,
    cancelledAt
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  let t1;
  if ($[0] !== t) {
    t1 = t("global.active");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = {
      label: t1,
      Icon: CheckIcon,
      className: "stroke-green"
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("global.cancelled");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== cancelledAt || $[7] !== user?.dateFormat || $[8] !== user?.timeFormat || $[9] !== user?.timezone) {
    t4 = cancelledAt ? "(" + dateTimeFormat(cancelledAt, {
      timeFormat: user?.timeFormat,
      dateFormat: user?.dateFormat,
      timeZone: user?.timezone
    }) + ")" : "";
    $[6] = cancelledAt;
    $[7] = user?.dateFormat;
    $[8] = user?.timeFormat;
    $[9] = user?.timezone;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = `${t3} ${t4}`;
  let t6;
  if ($[11] !== t5) {
    t6 = {
      label: t5,
      Icon: BlockedIcon,
      className: "fill-danger"
    };
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("global.completed");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = {
      label: t7,
      Icon: CheckIcon,
      className: "stroke-skeleton-primary"
    };
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t2 || $[18] !== t6 || $[19] !== t8) {
    t9 = {
      [SubscriptionStatusEnum.active]: t2,
      [SubscriptionStatusEnum.cancelled]: t6,
      [SubscriptionStatusEnum.completed]: t8
    };
    $[17] = t2;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const config = t9;
  const {
    label,
    Icon,
    className
  } = config[status];
  let t10;
  if ($[21] !== Icon || $[22] !== className) {
    t10 = <Icon className={className} />;
    $[21] = Icon;
    $[22] = className;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== label || $[25] !== t10) {
    t11 = <Tooltip mode="hover" label={label}>{t10}</Tooltip>;
    $[24] = label;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
const ContactSubscriptions = t0 => {
  const $ = _c(96);
  const {
    contactId,
    contact
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const {
    user,
    intlLocale
  } = useUser();
  const {
    setPagination,
    pagination,
    setLimit,
    limit,
    filter,
    setFilter
  } = useFilterPagination(defaultFilters, true);
  const t1 = filter.date[0] !== null;
  let t2;
  if ($[0] !== contactId || $[1] !== filter || $[2] !== limit || $[3] !== pagination || $[4] !== t1) {
    t2 = {
      contactId,
      limit,
      pagination,
      filter,
      shouldFetch: t1
    };
    $[0] = contactId;
    $[1] = filter;
    $[2] = limit;
    $[3] = pagination;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    contactSubscriptions,
    mutate
  } = useContactSubscriptions(t2);
  let t3;
  if ($[6] !== mutate || $[7] !== user?.timezone) {
    t3 = async subscription => {
      await mutate(data => data && {
        items: data.items.map(item => {
          if (item.id === subscription.id) {
            item = {
              ...item,
              status: SubscriptionStatusEnum.cancelled,
              subscriptionItems: item.subscriptionItems.map(subscriptionItem => ({
                ...subscriptionItem,
                status: SubscriptionStatusEnum.cancelled,
                ...(subscriptionItem.status === SubscriptionStatusEnum.active ? {
                  deactivatedAt: setDateWithTimezone(undefined, user?.timezone).toISOString()
                } : {})
              }))
            };
          }
          return item;
        }),
        hasMore: data.hasMore
      }, false);
    };
    $[6] = mutate;
    $[7] = user?.timezone;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onUnsubscribe = t3;
  let t4;
  if ($[9] !== contact || $[10] !== setFilter || $[11] !== user?.timezone) {
    t4 = () => {
      if (contact) {
        setFilter({
          date: getDateRangeSinceDate(contact.createdAt, user?.timezone)
        });
      }
    };
    $[9] = contact;
    $[10] = setFilter;
    $[11] = user?.timezone;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = user?.timezone;
  let t6;
  if ($[13] !== contact || $[14] !== setFilter || $[15] !== t5) {
    t6 = [contact, setFilter, t5];
    $[13] = contact;
    $[14] = setFilter;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  useEffect(t4, t6);
  let t7;
  if ($[17] !== t) {
    t7 = t("dashboard.contact_page.plan");
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = {
      key: "subscriptionItems",
      header: t7,
      onRender: _temp,
      onRenderSmall: _temp2,
      widthPercent: 80
    };
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t) {
    t9 = t("global.created_on");
    $[21] = t;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== user?.dateFormat || $[24] !== user?.timeFormat || $[25] !== user?.timezone) {
    t10 = subscription_2 => <Tooltip wrapperClassName="w-full" mode="hover" label={dateTimeFormat(subscription_2.createdAt, {
      timeFormat: user?.timeFormat,
      dateFormat: user?.dateFormat,
      timeZone: user?.timezone
    })}><span className="w-full truncate">{dateFormat(subscription_2.createdAt, {
          dateFormat: user?.dateFormat,
          timeZone: user?.timezone
        })}</span></Tooltip>;
    $[23] = user?.dateFormat;
    $[24] = user?.timeFormat;
    $[25] = user?.timezone;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t9) {
    t11 = {
      key: "createdAt",
      widthPercent: 20,
      header: t9,
      onRender: t10
    };
    $[27] = t10;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t8) {
    t12 = [t8, t11];
    $[30] = t11;
    $[31] = t8;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  const columns = t12;
  let t13;
  if ($[33] !== t) {
    t13 = t("dashboard.contact_page.subscriptions");
    $[33] = t;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t13) {
    t14 = <span className="text-[17px]">{t13}</span>;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const t15 = user?.timezone;
  let t16;
  if ($[37] !== t15) {
    t16 = getToday(t15);
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = contact?.createdAt;
  const t18 = user?.timezone;
  let t19;
  if ($[39] !== t17 || $[40] !== t18) {
    t19 = getDate(t17, t18);
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== contact?.createdAt || $[43] !== user?.timezone) {
    t20 = () => getDateRangeSinceDate(contact?.createdAt, user?.timezone);
    $[42] = contact?.createdAt;
    $[43] = user?.timezone;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== setFilter || $[46] !== setLimit || $[47] !== setPagination) {
    t21 = date => {
      setPagination({
        startFromId: undefined,
        order: OrderEnum.next
      });
      setLimit(10);
      setFilter({
        date
      });
    };
    $[45] = setFilter;
    $[46] = setLimit;
    $[47] = setPagination;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const t22 = user?.timezone;
  const t23 = user?.createdAt;
  let t24;
  if ($[49] !== filter.date || $[50] !== intlLocale || $[51] !== t16 || $[52] !== t19 || $[53] !== t20 || $[54] !== t21 || $[55] !== t22 || $[56] !== t23) {
    t24 = <DateRangePicker maxDate={t16} minDate={t19} getDefaultDate={t20} date={filter.date} onChange={t21} timezone={t22} createdAt={t23} intlLocale={intlLocale} />;
    $[49] = filter.date;
    $[50] = intlLocale;
    $[51] = t16;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== limit || $[59] !== setLimit) {
    t25 = {
      limit,
      setLimit
    };
    $[58] = limit;
    $[59] = setLimit;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== pagination || $[62] !== setPagination) {
    t26 = {
      pagination,
      setPagination
    };
    $[61] = pagination;
    $[62] = setPagination;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== t) {
    t27 = t("dashboard.user.affiliate");
    $[64] = t;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== t27) {
    t28 = {
      name: "affiliate",
      caption: t27,
      shouldRender: _temp3,
      setHref: _temp4,
      isNext: true,
      openInNew: false
    };
    $[66] = t27;
    $[67] = t28;
  } else {
    t28 = $[67];
  }
  let t29;
  if ($[68] !== t) {
    t29 = t("dashboard.contact_page.change_cc_info");
    $[68] = t;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== t29) {
    t30 = {
      name: "cc-info",
      caption: t29,
      shouldRender: _temp5,
      setHref: _temp6,
      openInNew: true
    };
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== t28 || $[73] !== t30) {
    t31 = [t28, t30];
    $[72] = t28;
    $[73] = t30;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  let t32;
  if ($[75] !== t) {
    t32 = t("dashboard.subscriptions.unsubscribe");
    $[75] = t;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== onUnsubscribe) {
    t33 = (opened, onClose, subscription_7, key) => <UnsubscribeModal key={key} opened={opened} onClose={onClose} subscription={subscription_7} onUnsubscribe={onUnsubscribe} />;
    $[77] = onUnsubscribe;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] !== t32 || $[80] !== t33) {
    t34 = [{
      name: "unsubscribe",
      caption: t32,
      onActionRender: t33,
      shouldRender: _temp7
    }];
    $[79] = t32;
    $[80] = t33;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  let t35;
  if ($[82] !== t31 || $[83] !== t34) {
    t35 = {
      openLink: t31,
      custom: t34
    };
    $[82] = t31;
    $[83] = t34;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  let t36;
  if ($[85] !== columns || $[86] !== contactSubscriptions || $[87] !== t25 || $[88] !== t26 || $[89] !== t35) {
    t36 = <Table limiter={t25} paginator={t26} actions={t35} data={contactSubscriptions} columns={columns} />;
    $[85] = columns;
    $[86] = contactSubscriptions;
    $[87] = t25;
    $[88] = t26;
    $[89] = t35;
    $[90] = t36;
  } else {
    t36 = $[90];
  }
  let t37;
  if ($[91] !== contactSubscriptions || $[92] !== t14 || $[93] !== t24 || $[94] !== t36) {
    t37 = <ListingLayout counter={false} data={contactSubscriptions} type="default" title={t14} customHeaderElement={t24}>{t36}</ListingLayout>;
    $[91] = contactSubscriptions;
    $[92] = t14;
    $[93] = t24;
    $[94] = t36;
    $[95] = t37;
  } else {
    t37 = $[95];
  }
  return t37;
};
export default ContactSubscriptions;
function _temp(subscription_0) {
  return <div className="flex w-full flex-col truncate">{subscription_0.subscriptionItems.map((el, idx) => <div key={idx} className={`py-3 ${idx !== 0 && "border-text-secondary/30 border-t"} ${idx === 0 && "pt-0"} ${idx + 1 === subscription_0.subscriptionItems.length && "pb-0"}`}><div className="flex items-center justify-between gap-1.5"><div className="flex items-center gap-1.5 truncate"><ContactPaymentsPaymentTooltip orderItem={el.orderItem} />{subscription_0.customer.referredBy && <Tooltip label={subscription_0.customer.referredBy.email ?? ""} mode="hover"><PercentIcon className="fill-text-tertiary" /></Tooltip>}</div><SubscriptionStatus status={subscription_0.status} cancelledAt={subscription_0.cancelledAt} /></div></div>)}</div>;
}
function _temp2(subscription_1) {
  return <div className="flex flex-col truncate">{subscription_1.subscriptionItems.map((el_0, idx_0) => <div key={idx_0} className={`truncate py-3 ${idx_0 !== 0 && "border-text-secondary/30 border-t"} ${idx_0 === 0 && "pt-0"} ${idx_0 + 1 === subscription_1.subscriptionItems.length && "pb-0"}`}><div className="flex items-center gap-1.5">{el_0.orderItem.offer && el_0.orderItem.offer.removedAt === null && el_0.orderItem.offer.funnelStep ? <LinkWithoutPrefetch rel="noreferrer" className="primary-link w-full truncate" href={getContactPaymentUrl(el_0.orderItem.offer.funnelStep.funnel.id, el_0.orderItem.offer.funnelStep.id)}>{el_0.orderItem.pricePlan ? el_0.orderItem.pricePlan.innerName : el_0.orderItem.productOrderItem && el_0.orderItem.productOrderItem.product.name}</LinkWithoutPrefetch> : <span>{el_0.orderItem.pricePlan.innerName}</span>}{subscription_1.customer.referredBy && <PercentIcon className="fill-text-tertiary" />}<SubscriptionStatus status={subscription_1.status} cancelledAt={subscription_1.cancelledAt} /></div></div>)}</div>;
}
function _temp3(subscription_3) {
  return subscription_3.customer.referredBy !== null;
}
function _temp4(subscription_4) {
  return `/affiliate-program/${subscription_4.customer.referredBy?.id}`;
}
function _temp5(subscription_5) {
  return subscription_5.changeCCLink !== null;
}
function _temp6(subscription_6) {
  return `${process.env.NEXT_PUBLIC_SIO_HOST_SCHEME}:${subscription_6.changeCCLink}`;
}
function _temp7(subscription_8) {
  return subscription_8.status === SubscriptionStatusEnum.active;
}